import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Trophy, Users, MapPin, DollarSign, X, Calendar } from 'lucide-react';
import ScopeSelector from '../components/ScopeSelector';
import { PageTooltip } from '../components/TooltipSystem';

const TYPES = [
  { id: 'referral_count', label: 'Referral', icon: Users, color: '#9C27B0', desc: 'Numero di amici invitati' },
  { id: 'visit_count', label: 'Visite', icon: MapPin, color: '#2196F3', desc: 'Numero di visite al locale' },
  { id: 'spend_amount', label: 'Spesa', icon: DollarSign, color: '#3ECF8E', desc: 'Importo speso in EUR' },
];

export default function AdminChallenges() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await adminApi.get('/admin/challenges');
      setChallenges(res.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa challenge?')) return;
    try {
      await adminApi.delete(`/admin/challenges/${id}`);
      toast.success('Challenge eliminata');
      fetchChallenges();
    } catch { toast.error('Errore nell\'eliminazione'); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-challenges-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Challenge</h1>
          <p className="text-[#64748B] text-sm">Sfide per incentivare i clienti</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-challenge-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity">
          <Plus size={16} /> Nuova
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>
      ) : challenges.length === 0 ? (
        <div className="text-center py-16">
          <Trophy size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-[#64748B] text-sm mb-4">Nessuna challenge configurata</p>
          <button onClick={() => setShowForm(true)} className="text-[#D44E20] text-sm font-medium">Crea la prima challenge</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {challenges.map(ch => {
            const typeInfo = TYPES.find(t => t.id === ch.challenge_type) || TYPES[0];
            const Icon = typeInfo.icon;
            return (
              <div key={ch.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`challenge-card-${ch.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${typeInfo.color}15` }}>
                      <Icon size={18} style={{ color: typeInfo.color }} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1A1A1A] text-sm">{ch.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ch.is_onboarding ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {ch.is_onboarding ? 'Onboarding' : 'Stagionale'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ch.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {ch.is_active ? 'Attiva' : 'Disattiva'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(ch); setShowForm(true); }} data-testid={`edit-challenge-${ch.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-[#64748B] hover:bg-slate-100 transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(ch.id)} data-testid={`delete-challenge-${ch.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-[#EF4444] hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                {ch.description && <p className="text-[#64748B] text-xs mb-3">{ch.description}</p>}
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#64748B]">Tipo</span>
                    <span className="font-medium text-[#1A1A1A]">{typeInfo.label} — obiettivo: {ch.target}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#64748B]">Premio</span>
                    <span className="font-medium text-[#D44E20]">{ch.reward_label || `${ch.reward_value} punti`}</span>
                  </div>
                  {ch.start_date && (
                    <div className="flex justify-between text-xs">
                      <span className="text-[#64748B]">Periodo</span>
                      <span className="text-[#1A1A1A]">{ch.start_date} → {ch.end_date || '∞'}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ChallengeForm
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchChallenges(); }}
        />
      )}
    </div>
  );
}

function ChallengeForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: editing?.name || '',
    description: editing?.description || '',
    challenge_type: editing?.challenge_type || 'visit_count',
    target: editing?.target || 5,
    reward_type: 'points',
    reward_value: editing?.reward_value || 100,
    reward_label: editing?.reward_label || '',
    is_onboarding: editing?.is_onboarding || false,
    start_date: editing?.start_date || '',
    end_date: editing?.end_date || '',
    is_active: editing?.is_active ?? true,
  });
  const [scope, setScope] = useState(editing?.scope || 'brand');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Inserisci un nome'); return; }
    if (form.target <= 0) { toast.error('L\'obiettivo deve essere > 0'); return; }
    setSaving(true);
    try {
      const payload = { ...form, scope };
      if (editing) {
        await adminApi.put(`/admin/challenges/${editing.id}`, payload);
        toast.success('Challenge aggiornata');
      } else {
        await adminApi.post('/admin/challenges', payload);
        toast.success('Challenge creata');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <PageTooltip page="challenges" />
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="challenge-form-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-bold text-lg text-[#1A1A1A]">{editing ? 'Modifica' : 'Nuova'} Challenge</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#64748B] hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-1">Nome *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="challenge-name"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" placeholder="Es. Habitué del mese" />
          </div>
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-1">Descrizione</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="challenge-desc"
              className="w-full h-20 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none resize-none" placeholder="Descrizione della sfida..." />
          </div>
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-2">Tipo di challenge *</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(t => (
                <button key={t.id} onClick={() => setForm(f => ({ ...f, challenge_type: t.id }))} data-testid={`type-${t.id}`}
                  className={`p-3 rounded-xl border text-center transition-all ${form.challenge_type === t.id ? 'border-[#D44E20] bg-[#D44E20]/5' : 'border-slate-200 hover:border-gray-300'}`}>
                  <t.icon size={20} className="mx-auto mb-1" style={{ color: form.challenge_type === t.id ? '#D44E20' : '#888' }} />
                  <p className="text-xs font-medium" style={{ color: form.challenge_type === t.id ? '#D44E20' : '#666' }}>{t.label}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-1">
                Obiettivo ({form.challenge_type === 'spend_amount' ? 'EUR' : 'numero'}) *
              </label>
              <input type="number" value={form.target} onChange={e => setForm(f => ({ ...f, target: Number(e.target.value) }))} data-testid="challenge-target"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-1">Premio (punti) *</label>
              <input type="number" value={form.reward_value} onChange={e => setForm(f => ({ ...f, reward_value: Number(e.target.value) }))} data-testid="challenge-reward"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-1">Etichetta premio</label>
            <input value={form.reward_label} onChange={e => setForm(f => ({ ...f, reward_label: e.target.value }))} data-testid="challenge-reward-label"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" placeholder="Es. 200 punti bonus" />
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.is_onboarding} onChange={e => setForm(f => ({ ...f, is_onboarding: e.target.checked }))} data-testid="challenge-onboarding" className="sr-only peer" />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#D44E20] transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">Challenge di onboarding</p>
              <p className="text-xs text-[#64748B]">Sempre attiva per tutti i nuovi clienti, senza scadenza</p>
            </div>
          </div>
          {!form.is_onboarding && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] font-medium block mb-1">Data inizio</label>
                <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="challenge-start"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] font-medium block mb-1">Data fine</label>
                <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="challenge-end"
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} data-testid="challenge-active" className="sr-only peer" />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#3ECF8E] transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <p className="text-sm text-[#1A1A1A]">Attiva</p>
          </div>
          <ScopeSelector value={scope} onChange={setScope} />
        </div>
        <div className="p-5 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 h-10 bg-slate-100 text-[#64748B] text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors">Annulla</button>
          <button onClick={handleSave} disabled={saving} data-testid="challenge-save-btn"
            className="flex-1 h-10 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
            {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Crea')}
          </button>
        </div>
      </div>
    </div>
  );
}
